import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Layout from "../../../components/Layout/Layout";
import DiscountHubService from "../../../services/DiscountHubService";
import { HollowDotsSpinner } from "react-epic-spinners";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import fire from "../../../utils/Toastr";

export default function EditReferralCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const user = sessionStorage.getItem("user");
  let modifiedBy = "admin";
  try {
    if (user) {
      const userObj = JSON.parse(user);
      modifiedBy = userObj.userName || userObj.email || "admin";
    }
  } catch (e) {
    // ignore
  }

  const [formValues, setFormValues] = useState({
    id: id || "",
    referralCode: "",
    referralName: "",
    referralAmount: "",
    modifiedBy: modifiedBy,
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) {
        setFetchLoading(false);
        fire("error", "Referral code ID is missing");
        navigate("/discount-hub/referral-codes");
        return;
      }
      try {
        const resp = await DiscountHubService.getReferralCodeDetails(id);
        if (resp && resp.data) {
          const data = resp.data;
          setFormValues({
            id: data.id,
            referralCode: data.referralCode || "",
            referralName: data.referralName || "",
            referralAmount: data.referralAmount || 0,
            modifiedBy: modifiedBy,
          });
        } else {
          fire("error", resp?.message || "Failed to fetch referral code details");
        }
      } catch (error) {
        console.error("Error fetching referral code details:", error);
        fire("error", "Failed to fetch referral code details");
      } finally {
        setFetchLoading(false);
      }
    };
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formValues,
        referralAmount: parseFloat(formValues.referralAmount) || 0,
      };
      const resp = await DiscountHubService.updateReferralCode(payload);
      if (resp && resp.status !== false) {
        fire("success", resp.message || "Referral code updated successfully");
        navigate("/discount-hub/referral-codes");
      } else {
        fire("error", resp?.message || "Failed to update referral code");
      }
    } catch (error) {
      console.error("Error updating referral code:", error);
      fire("error", "Failed to update referral code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Edit Referral Code" pageTag="discount-hub" current="/discount-hub/referral-codes">
      <React.Fragment>
        <main>
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Link
                to="/discount-hub/referral-codes"
                className="inline-flex items-center text-sm text-red-600 hover:text-red-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to Referral Codes
              </Link>
            </div>

            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Edit Referral Code
                </h2>
              </div>
              {fetchLoading ? (
                <div className="flex justify-center py-12">
                  <HollowDotsSpinner size={50} color="#4D627E" />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
                  <div>
                    <label
                      htmlFor="referralCode"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Referral Code
                    </label>
                    <input
                      type="text"
                      name="referralCode"
                      id="referralCode"
                      required
                      value={formValues.referralCode}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="e.g., PTB001"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="referralName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Referral Name
                    </label>
                    <input
                      type="text"
                      name="referralName"
                      id="referralName"
                      required
                      value={formValues.referralName}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="e.g., Becon Daniel"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="referralAmount"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Referral Amount
                    </label>
                    <input
                      type="number"
                      name="referralAmount"
                      id="referralAmount"
                      required
                      value={formValues.referralAmount}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="0"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Link
                      to="/discount-hub/referral-codes"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-60"
                    >
                      {loading ? (
                        <HollowDotsSpinner size={12} color="#FFFFFF" className="mr-2" />
                      ) : null}
                      {loading ? "Updating..." : "Update Referral Code"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </main>
      </React.Fragment>
    </Layout>
  );
}
